import { AlertTriangle, Power, SlidersHorizontal } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { Instrument } from "../data/instruments";
import { getTuningById, instruments } from "../data/instruments";
import { useI18n } from "../i18n/LocaleContext";
import { getLocaleConfig, localeCodes, localizePath, stripLocalePrefix, type LocaleCode } from "../i18n/locales";
import { useTimbroamState } from "../lib/appState";
import { TuningSelector } from "./TuningSelector";
import { VariantSelector } from "./VariantSelector";

type AudioState = "locked" | "ready" | "failed";

interface TopBarProps {
  selectedInstrument: Instrument;
  audioState: AudioState;
  onEnableAudio: () => void;
  showInstrumentBar?: boolean;
}

export function TopBar({ selectedInstrument, audioState, onEnableAudio, showInstrumentBar = true }: TopBarProps) {
  const { state, dispatch } = useTimbroamState();
  const { locale, t, localize } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTuning = getTuningById(selectedInstrument.id, state.selectedTuningId);
  const audioLabelByState: Record<AudioState, string> = {
    locked: t.audio.enable,
    ready: t.audio.ready,
    failed: t.audio.retry
  };
  const handleLanguageChange = (nextLocale: LocaleCode) => {
    navigate(localizePath(nextLocale, stripLocalePrefix(location.pathname)));
  };

  return (
    <header className="top-bar" aria-label="Primary Timbroam controls">
      <div className="top-app-row">
        <div className="brand-lockup">
          <NavLink to={localize("/")} className="brand-mark" aria-label="Go to Timbroam home">
            <span>
              <strong>Timbroam</strong>
              <small>{t.brandTagline}</small>
            </span>
          </NavLink>
          <nav className="main-nav" aria-label="Main navigation">
            <NavLink to={localize("/")} end>{t.nav.play}</NavLink>
            <NavLink to={localize("/instruments")}>{t.nav.instruments}</NavLink>
            <NavLink to={localize("/about")}>{t.nav.about}</NavLink>
            <NavLink to={localize("/qa")}>{t.nav.qa}</NavLink>
            <NavLink to={localize("/audio-lab")} className="developer-nav-link">
              {t.nav.audioLab}
            </NavLink>
          </nav>
        </div>
        <div className="top-controls">
          <select
            className="language-select"
            aria-label="Select language"
            value={locale}
            onChange={(event) => handleLanguageChange(event.currentTarget.value as LocaleCode)}
          >
            {localeCodes.map((code) => {
              const optionLocale = getLocaleConfig(code);

              return (
                <option key={code} value={code}>
                  {optionLocale.nativeName}
                </option>
              );
            })}
          </select>

          <button
            className={`audio-chip is-${audioState}`}
            type="button"
            aria-label="Activate audio engine"
            data-testid="audio-enable-button"
            onClick={onEnableAudio}
          >
            <Power size={16} aria-hidden="true" />
            <span>{audioLabelByState[audioState]}</span>
          </button>
          <span className={`audio-status is-${audioState}`} data-testid="audio-status" aria-live="polite">
            {audioState === "failed" ? t.audio.unavailable : audioState === "ready" ? t.audio.statusReady : t.audio.statusLocked}
          </span>
        </div>
      </div>

      {showInstrumentBar ? (
        <div className="instrument-control-bar" aria-label="Instrument setup">
          <label className="control-field instrument-picker">
            <span>{t.controls.instrument}</span>
            <select
              aria-label="Select instrument"
              data-testid="instrument-select"
              value={state.selectedInstrumentId}
              onChange={(event) => dispatch({ type: "selectInstrument", instrumentId: event.currentTarget.value })}
            >
              {instruments.map((instrument) => (
                <option key={instrument.id} value={instrument.id}>
                  {instrument.name}
                </option>
              ))}
            </select>
          </label>

          <VariantSelector
            instrument={selectedInstrument}
            value={state.selectedVariantId}
            onChange={(variantId) => dispatch({ type: "selectVariant", variantId })}
          />

          <TuningSelector
            instrument={selectedInstrument}
            value={state.selectedTuningId}
            onChange={(tuningId) => dispatch({ type: "selectTuning", tuningId })}
          />

          <div className={`setup-badge ${selectedTuning.isApproximation ? "is-warning" : "is-reviewed"}`} role="note">
            {selectedTuning.isApproximation ? <AlertTriangle size={16} aria-hidden="true" /> : <SlidersHorizontal size={16} aria-hidden="true" />}
            <span>{selectedTuning.isApproximation ? t.controls.approxTuning : t.controls.reviewedTuning}</span>
          </div>
        </div>
      ) : null}
    </header>
  );
}
